import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { ColorsUtils } from "src/app/utils/colors.utils";

export interface ISelectItem {
  id?: string;
  value?: any;
  text: string;
  color?: string;
}

export interface ISelectSearchOptions {
  placeholder?: string;
  allowAdd?: boolean;
  multiple?: boolean;
  uniqueTags?: boolean;
  filter?: "case-sensitive" | "case-insensitive";
  minChars?: number;
  autoColor?: boolean;
  defaultColor?: string;
  colors?: Array<string>;
}

const DEFAULT_OPTIONS: ISelectSearchOptions = {
  placeholder: "Rechercher...",
  allowAdd: true,
  multiple: false,
  uniqueTags: true,
  filter: "case-insensitive",
  minChars: 1,
  autoColor: true,
  defaultColor: "#48b9b2",
  colors: [
    "#800000", "#FF0000", "#800080", "#FF00FF", "#008000",
    "#808000", "#000080", "#0000FF", "#008080", "#935116", "#48b9b2"
  ]
};

@Component({
  selector: 'app-select-tags',
  templateUrl: './select-tags.component.html',
  styleUrls: ['./select-tags.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SelectTagsComponent)
  }]
})
export class SelectTagsComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  @Input() disabled: boolean = false;
  @Input() itemsSource: Array<ISelectItem> = [];

  @Output() itemsSourceChange = new EventEmitter<Array<ISelectItem>>();

  private _options: Partial<ISelectSearchOptions> = { ...DEFAULT_OPTIONS };
  @Input()
  set options(value: Partial<ISelectSearchOptions>) {
    this._options = { ...DEFAULT_OPTIONS, ...(value || {}) };
  }
  get options(): Partial<ISelectSearchOptions> {
    return this._options;
  }

  @Output() add = new EventEmitter<ISelectItem>();

  @ViewChild("searchInput", { static: false }) searchInput!: ElementRef;
  @ViewChild("wrapper", { static: false }) wrapper!: ElementRef;

  selected: Array<ISelectItem> = [];
  filteredItems: Array<ISelectItem> = [];
  searchText: string = "";
  isOpen: boolean = false;

  private searchSubject = new Subject<string>();
  private normalizedCache = new WeakMap<ISelectItem, string>();
  private lastWrittenSignature: string | null = null;

  constructor() {
    this.searchSubject.pipe(
      debounceTime(120),
      distinctUntilChanged()
    ).subscribe(() => this.runFilter());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemsSource']) {
      this.normalizedCache = new WeakMap();
      this.runFilter();
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.wrapper && !this.wrapper.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.onTouched();
      }
    }
  }

  toggleList(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.runFilter();
      setTimeout(() => this.focusInput());
    } else {
      this.onTouched();
    }
  }

  openList(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.runFilter();
    setTimeout(() => this.focusInput());
  }

  private isSameItem(a: ISelectItem, b: ISelectItem): boolean {
    if (!a || !b) return false;
    if (a.id != null && b.id != null) return a.id === b.id;
    return this.getNormalizedText(a) === this.getNormalizedText(b);
  }

  private getNormalizedText(item: ISelectItem): string {
    if (!this.normalizedCache.has(item)) {
      const trimmed = String(item?.text ?? "").trim();
      this.normalizedCache.set(item, this.options.filter === "case-insensitive" ? trimmed.toLocaleLowerCase() : trimmed);
    }
    return this.normalizedCache.get(item)!;
  }

  private normalizePattern(pattern: string): string {
    const trimmed = String(pattern || "").trim();
    return this.options.filter === "case-insensitive" ? trimmed.toLocaleLowerCase() : trimmed;
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchText);
  }

  private runFilter(): void {
    const pattern = String(this.searchText || "").trim();
    const normalizedPattern = this.normalizePattern(pattern);

    let source = this.itemsSource || [];
    if (this.options.multiple && this.options.uniqueTags) {
      source = source.filter(item => !this.selected.some(s => this.isSameItem(s, item)));
    }

    this.filteredItems = !pattern
      ? source
      : source.filter(item => this.getNormalizedText(item).includes(normalizedPattern));
  }

  filter(): void {
    this.runFilter();
  }

  canAddNew(): boolean {
    if (!this.options.allowAdd) return false;
    const pattern = String(this.searchText || "").trim();
    if (pattern.length < (this.options.minChars ?? 1)) return false;

    const candidate: ISelectItem = { text: pattern };
    const exists = (this.itemsSource || []).some(item => this.isSameItem(item, candidate));
    return !exists;
  }

 addNewItem(): void {
    const value = (this.searchText || '').trim();
    if (!value) return;

    const newItem: ISelectItem = {
      text: value,
      color: this.options.autoColor
        ? this._getColor()
        : (this.options.defaultColor || "#48b9b2")
    };

    this.itemsSource = [...this.itemsSource, newItem];
    this.itemsSourceChange.emit(this.itemsSource);
    this.add.emit(newItem);

    if (this.options.multiple) {
      this.selected = [...this.selected, newItem];
    } else {
      this.selected = [newItem];
    }

    this.searchText = "";
    this.commitChange();
    this.runFilter();
    this.closeList();
  }

  pickItem(item: ISelectItem): void {
    if (!item) return;

    const exists = this.selected.some(s => this.isSameItem(s, item));
    if (exists) return;

    const selectedItem = {
      ...item,
      color: item.color || this._getColor()
    };

    if (this.options.multiple) {
      this.selected = [...this.selected, selectedItem];
    } else {
      this.selected = [selectedItem];
    }

    this.searchText = "";
    this.commitChange();
    this.runFilter();
    this.closeList();
  }


  removeItem(index: number, event?: Event): void {
    event?.stopPropagation();
    this.selected = this.selected.filter((_, i) => i !== index);
    this.commitChange();
    this.onTouched();
    this.runFilter();
  }

handleKeyEnter($event: KeyboardEvent): void {
    if ($event.key === "Enter") {
      $event.preventDefault();
      if (this.filteredItems.length === 1) {
        this.pickItem(this.filteredItems[0]);
      } else if (this.canAddNew()) {
        this.addNewItem();
      }
    } else if ($event.key === "Escape") {
      this.closeList();
      this.searchInput?.nativeElement.blur();
    }
  }

  private closeList(): void {
    this.isOpen = false;
    this.onTouched();
  }


  focusInput(): void {
    if (this.searchInput) this.searchInput.nativeElement.focus();
  }

  displayText(item: ISelectItem | undefined | null): string {
    return item && item.text != null ? String(item.text) : "";
  }

  trackByItem(_index: number, item: ISelectItem): string {
    return item?.id ?? item?.text ?? String(_index);
  }

  private _getColor(): string {
    return this.options.autoColor
      ? (this.options.colors ? ColorsUtils.random(this.options.colors) : "#48b9b2")
      : this.options.defaultColor || "#48b9b2";
  }

  private getValue(): any {
    return this.options.multiple ? this.selected : (this.selected[0] || null);
  }

  private signatureOf(value: any): string {
    const arr: ISelectItem[] = Array.isArray(value) ? value : (value ? [value] : []);
    return arr
      .map(v => (typeof v === "string" ? v : (v?.id ?? v?.text ?? "")))
      .join("|");
  }

  private commitChange(): void {
    const value = this.getValue();
    this.lastWrittenSignature = this.signatureOf(value);
    this.onChange(value);
  }

  // --- ControlValueAccessor ---
  writeValue(value: any): void {
    const incomingSignature = this.signatureOf(value);

    if (this.lastWrittenSignature !== null && incomingSignature === this.lastWrittenSignature) {
      return;
    }

    const normalize = (v: any): ISelectItem | null => {
      if (v == null) return null;
      const item: ISelectItem = typeof v === "string" ? { text: v } : { ...v };
      if (!item.color) item.color = this.options.autoColor ? this._getColor() : (this.options.defaultColor || "");
      return item;
    };

    if (this.options.multiple) {
      this.selected = Array.isArray(value) ? value.map(normalize).filter((v): v is ISelectItem => !!v) : [];
    } else {
      const single = Array.isArray(value) ? value[0] : value;
      const normalized = normalize(single);
      this.selected = normalized ? [normalized] : [];
    }

    this.lastWrittenSignature = incomingSignature;
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };


 
}